import { SCREENSHOTS } from './images'

/**
 * One source of truth for everything the site says.
 *
 * Rules that matter here (Fran's, do not undo):
 *  - No company names, employers or clients. Work is described by domain.
 *  - No full name: `Fran` and/or `jfrader` only.
 *  - No email address and no LinkedIn. GitHub is the only contact link.
 *  - No em dashes.
 *  - No invented metrics, users, revenue or scale.
 */

export const ABOUT = [
  "Full-stack software architect specialized in Frontend. Building clean, maintainable and performant end to end web applications for more than 15 years.",
  "I have designed, led and shipped fintech and crypto payments, ecommerce, games, media, marketplaces and consumer products, mostly in TypeScript, React, Node.js, and PostgreSQL.",
  "My extensive experience allows me to know all the nuances about building apps that are bug-free, easy to use, performant, maintainable and secure. I prefer working close to the product, so that engineering decisions stay practical.",
  "On the backend side, I have designed and built database schemas, migrations, frameworks, code generators, REST API's, GraphQL API's, WebSocket servers, multiplayer game systems and more.",
  "Similarly, I have worked end-to-end integrating error-tracking, analytics suites, authentication patterns, CI/CD pipelines, automation and integration tests and more.",
  "In the new AI era, I was able to migrate entire codebases and technical debt to newer technologies and solutions. Designed and integrated AI chat and automated AI workflows into production applications.",
  "Also, by building my own agentic workflow with custom and vendor MCP servers and Skills, I was able to orchestrate multiple AI models together to ship incredibly fast and maintain the code quality that characterizes me.",
];

export const WORK = [
  {
    when: "2023 - 2026",
    what: "Energy payments platform",
    body: "Frontend for a real-time automated B2B payments platform: banking transfers and ACH, billing, settlements, payment tracking and audit dashboards. Built the frontend integrations for Bitcoin and Lightning payments, plus the shared component library and the public Next.js site. Shipped under SOC 2 controls, and built the UX for the AI assistant and AI-assisted settlement reconciliation.",
    stack: "React · TypeScript · Next.js · Rust / SQLx APIs · Sentry",
  },
  {
    when: "2023",
    what: "Crowdfunding platform",
    body: "Donation and crowdfunding flows: Lightning checkout, mobile QR funding and real-time donation updates. Built the content creator's admin panel and content editor, with accessibility, SEO, performance and security work on top.",
    stack: "React · TypeScript · Lightning · REST APIs",
  },
  {
    when: "2022",
    what: "Consumer Bitcoin wallet",
    body: "React and TypeScript on a Bitcoin wallet Chrome extension: wallet UI and payment flows.",
    stack: "React · TypeScript · browser extension",
  },
  {
    when: "2020 - 2022",
    what: "Marketplace platform and admin",
    body: "Entered as a frontend engineer and led the admin dashboard for inventory and delivery management in React and Apollo GraphQL. Then moved to the backend as well and led the team: Node.js, Knex, PostgreSQL and GraphQL APIs. Also contributed to the consumer side: a Gatsby PWA storefront and a Next.js sales interface.",
    stack: "React · Apollo GraphQL · Node.js · Knex · PostgreSQL · Gatsby · Next.js",
  },
  {
    when: "2019 - 2020",
    what: "Analytics and tagging for travel bookings",
    body: "Tealium and Adobe Analytics tagging across several React apps for a travel bookings client: dataLayer and eVar tagging for bookings, cruises and activities.",
    stack: "React · Tealium · Adobe Analytics",
  },
  {
    when: "2019 - 2020",
    what: "Terrain visualization",
    body: "Contributed to a terrain visualization product rendering real-world elevation data, built on deck.gl and Mapbox.",
    stack: "React · deck.gl · Mapbox",
  },
  {
    when: "2018 - 2019",
    what: "Vehicle-parts marketplace startup",
    body: "Co-founded a vehicle-parts marketplace. Led the technical direction and helped shape the product. Built the application end to end.",
    stack: "TypeScript · React · Node.js · NestJS · MongoDB",
  },
  {
    when: "2018 - 2019",
    what: "Internal entertainment apps",
    body: "Internal apps in Angular, with Storybook, MobX and MongoDB.",
    stack: "Angular · Storybook · MobX · MongoDB",
  },
  {
    when: "2018",
    what: "Streaming platform media centre",
    body: "Built a streaming platform's media centre in React, Next.js, TypeScript and Redux.",
    stack: "React · Next.js · TypeScript · Redux",
  },
  {
    when: "2017 - 2018",
    what: "Learning app for iOS and Android",
    body: "An Angular and Ionic learning app, shipped to iOS and Android.",
    stack: "Angular · Ionic · TypeScript",
  },
  {
    when: "2016 - 2017",
    what: "National newspaper frontend and API",
    body: "Built the frontend and the Node middleware API in front of its decoupled Drupal CMS, including the caching and invalidation layer.",
    stack: "Node.js · DustJS · SCSS · Redis · Drupal",
  },
  {
    when: "2015 - 2016",
    what: "Ticketing web platforms",
    body: "Ticketing web platforms, from the booking flow through to the admin behind it.",
    stack: "PHP · JavaScript · CoffeeScript",
  },
  {
    when: "2009 - 2015",
    what: "Real-estate management product",
    body: "A property management system for a real-estate agency, built from scratch from the data model to the interface: listings, photos, calendar, search and a full admin panel. Live for years, and being migrated to Next.js and Node.js.",
    stack: "PHP · MySQL · JavaScript · jQuery · Linux",
  },
  {
    when: "2009 - 2015",
    what: "E-commerce and custom admin",
    body: "A full production e-commerce site for a magazines and songbooks wholesaler: catalogue, orders, PDF and photo management, and a custom admin built from scratch.",
    stack: "PHP · MySQL · JavaScript · jQuery",
  },
  {
    when: "Early freelance work",
    what: "Motorcycle catalogue website",
    body: "Adapted an Italian motorcycle brand's frontend design for its Argentine catalogue website, and built its admin CMS with my CodeIgniter generator. The site included model pages, dealer listings, used bikes and PDF downloads.",
    stack: "PHP · MySQL · HTML · CSS · JavaScript",
  },
  {
    when: "Early freelance work",
    what: "Fantasy rugby game",
    body: "Built a fantasy rugby game end to end for an Argentine rugby club, including the UI, team selection, captain selection, rankings and admin tools for competitions and player scoring.",
    stack: "PHP · MySQL · JavaScript · jQuery",
  },
  {
    when: "2009 - 2015",
    what: "Admin generator and tools",
    body: "A CodeIgniter-based generator that turned database definitions and relations into a working admin CMS, used to ship client projects and an accounting system. Also image and calendar tooling.",
    stack: "PHP · MySQL · CodeIgniter · JavaScript",
  },
];

/**
 * Non-game live products, shown on /products. `repo` is the GitHub repository
 * it is built from, so /projects can leave it out of the live list. The games
 * live in GAMES below.
 * Optional `source` (full public GitHub URL) renders as GitHub icon + label
 * link on the card; set only for public repos (private ones get none).
 */
export const PRODUCTS = [
  {
    name: "EL HECHO",
    tag: "news · live",
    repo: "el-hecho",
    url: "https://elhecho.com.ar",
    img: SCREENSHOTS.elhecho,
    desc: "Autonomous Argentine newspaper. It ingests news wires from local outlets and official sources, groups the same event, extracts the factual claims and proves each one across independent outlets. Facts it can stand behind get published, contradictions run in two columns, single-source claims are attributed, and opinions stay out. The claims are extracted and corroborated with a judgment model; an LLM then writes the note under the paper's own style.",
    stack: "React · TypeScript · TanStack Start · PostgreSQL · judgment model · LLM",
  },
  {
    name: "Gamestruments",
    tag: "audio · live",
    repo: "gamestruments",
    url: "https://gamestruments.gurisitos.games",
    img: SCREENSHOTS.gamestruments,
    desc: "Adaptive gameplay music library. Deterministic procedural music, with music controls, crossover between genres and a score debugger.",
    stack: "Rust · TypeScript · GDScript",
    source: "https://github.com/jfrader/gamestruments",
  },
  {
    name: "Chat With a Website",
    tag: "llm · live",
    repo: "chat-with-a-website",
    url: "https://chat-with-a-website.onrender.com",
    img: SCREENSHOTS.chat,
    desc: "Live LLM product: OpenAI-compatible SDK with streamed SSE responses and prompt-injection defence.",
    stack: "React 19 · Node.js",
    source: "https://github.com/jfrader/chat-with-a-website",
  },
];

/**
 * Published packages. `repo` is the GitHub repository each one is built from,
 * so /projects can leave it out of the live list. Tools and libraries are not
 * listed separately on purpose: they are repositories, and the live list
 * already orders them by stars and by how recently they were pushed.
 */
export const PACKAGES = [
  {
    name: "@jfrader/observability",
    repo: "observability",
    url: "https://www.npmjs.com/package/@jfrader/observability",
    desc: "Pluggable browser and server error tracking and analytics behind one tiny API. Sentry and PostHog as optional providers, privacy redaction built in, no runtime dependencies.",
  },
  {
    name: "@jfrader/changelog",
    repo: "changelog",
    url: "https://www.npmjs.com/package/@jfrader/changelog",
    desc: "One markdown file per change, a CLI and a build that emit CHANGELOG.md and JSON, and a self-contained page for readers. No runtime dependencies.",
  },
  {
    name: "@jfrader/pwa-updater",
    repo: "pwa-updater",
    url: "https://www.npmjs.com/package/@jfrader/pwa-updater",
    desc: "Safe new-deploy detection and once-per-version reload prompts for small PWAs. No runtime dependencies.",
  },
];

export const SKILLS: [string, string][] = [
  ["frontend", "TypeScript · React · Next.js · TanStack Query · Redux and Context · Tailwind CSS · accessibility · performance · SEO · PWA"],
  ["backend and data", "Node.js · REST · GraphQL · OpenAPI · WebSockets and Socket.IO · PostgreSQL · Redis · Prisma · Rust"],
  ["delivery", "Linux · Docker · CI/CD · staging environments · review gates · Sentry · PostHog · Prometheus · backups and rollback checks"],
  ["security and compliance", "SOC 2 controls · XSS, CSRF and CSP · prompt-injection hardening"],
  ["domains", "fintech and payments · Bitcoin and Lightning · e-commerce and marketplaces · media and streaming · crowdfunding · e-learning · games"],
  ["practice", "code and design review · AI-assisted development with OpenCode and Engram · MCP servers I use and build"],
];

/** GitHub is the only contact link. */
export const CONTACT = [
  ["github", "github.com/jfrader", "https://github.com/jfrader"],
] as const;

/* The one profile link, named once: the terminal and the shell both render it,
   and a second copy is how two of them drift apart. */
export const GITHUB_HANDLE = CONTACT[0][1];
export const GITHUB_URL = CONTACT[0][2];

/**
 * The contact address is assembled from parts at runtime and only rendered after
 * a click, so no address literal exists in the served HTML or in the JS bundle
 * for a crawler to harvest. Keep the two halves separate: joining them into one
 * string anywhere would defeat this, and the content guard will fail the build.
 */
const CONTACT_USER = 'me'
const CONTACT_DOMAIN = 'jfrader.com'

export const contactEmail = () => `${CONTACT_USER}@${CONTACT_DOMAIN}`

/** The studio behind the games. */
export const STUDIO = {
  name: "Gurisitos Games",
  url: "https://gurisitos.games",
  desc: "the studio I run",
};

/**
 * Games, shown as cards on /products. `img` and `stack` are optional: Civiku
 * has no screenshot and no recorded stack yet. `repos` names the GitHub
 * repositories that back a game, so /projects can leave them out of its list
 * rather than showing the same work twice.
 * Optional `source` (full public GitHub URL) renders as GitHub icon + label
 * link on the card; set only for public repos (private ones get none).
 */
export const GAMES = [
  {
    name: "Trucoshi",
    tag: "game · live",
    url: "https://trucoshi.com",
    repos: ["trucoshi", "trucoshi-client"],
    img: SCREENSHOTS.trucoshi,
    desc: "Online multiplayer card game, built and operated end to end. Real-time turns over WebSockets; the server owns the game state.",
    stack: "React · Node.js · WebSockets · PostgreSQL · Redis",
    source: "https://github.com/jfrader/trucoshi",
  },
  {
    name: "Pocket Circuit",
    tag: "game · open source",
    url: "https://github.com/jfrader/pocket-circuit",
    repos: ["pocket-circuit"],
    img: SCREENSHOTS.pocketcircuit,
    desc: "Godot 4.7 top-down 2D miniature arcade racer. Tiny vehicles race through oversized everyday environments (kitchen, workshop, office) feeding a persistent garage.",
    stack: "Godot 4.7 · GDScript",
    source: "https://github.com/jfrader/pocket-circuit",
  },
  {
    name: "MiFulbo",
    tag: "game · live",
    url: "https://mifulbo.com",
    img: SCREENSHOTS.mifulbo,
    desc: "Persistent football management game. A Fastify API and worker own mutations, scheduling, simulation and settlement.",
    stack: "TypeScript · Fastify · PostgreSQL · Redis",
  },
  {
    name: "Huertoku",
    tag: "game · live",
    url: "https://huertoku.com",
    img: SCREENSHOTS.huertoku,
    desc: "Bilingual daily garden puzzle with deterministic boards and share cards, plus a separate billing service for subscriptions.",
    stack: "React · TypeScript · PWA · Stripe · Mercado Pago",
  },
  {
    name: "Civiku",
    tag: "game · live",
    url: "https://civiku.gurisitos.games",
    desc: "Bilingual daily hex-grid city-building puzzle: deterministic seeded generation, hand-drawn SVG, and guaranteed solvability.",
  },
];

/**
 * Everything shown on /products: products and games in one list. The two
 * flagships lead; the rest keeps each list's own order.
 */
const FEATURED = new Set(["EL HECHO", "Trucoshi"]);
const ALL_PRODUCTS = [...PRODUCTS, ...GAMES];
export const CATALOG = [
  ...ALL_PRODUCTS.filter((item) => FEATURED.has(item.name)),
  ...ALL_PRODUCTS.filter((item) => !FEATURED.has(item.name)),
];

export const short = (url: string) => url.replace(/^https?:\/\//, "");
